// Mock implementation of RecordProvider for testing

import type { GristRecord, ColumnMappings } from '../types';
import type { RecordProvider, RecordProviderEvents } from './record-provider';

/**
 * Configuration for MockRecordProvider
 */
export interface MockRecordProviderConfig {
  /** Initial records to provide */
  records?: GristRecord[];
  /** Column mappings */
  mappings?: ColumnMappings;
  /** Saved options/config */
  options?: Record<string, unknown>;
  /** Delay in ms before emitting records (simulates network) */
  delay?: number;
}

/**
 * Mock RecordProvider for testing without Grist API
 */
export class MockRecordProvider implements RecordProvider {
  private records: GristRecord[] = [];
  private mappings: ColumnMappings = {};
  private options: Record<string, unknown> = {};
  private delay: number;
  private ready = false;
  private events: RecordProviderEvents = {};
  private selectedRecordId: number | null = null;

  constructor(config: MockRecordProviderConfig = {}) {
    this.records = config.records || [];
    this.mappings = config.mappings || {};
    this.options = config.options || {};
    this.delay = config.delay || 0;
  }

  /**
   * Initialize mock provider and emit initial data
   */
  initialize(events: RecordProviderEvents): void {
    this.events = events;
    this.ready = true;

    // Emit initial records after optional delay
    if (this.delay > 0) {
      setTimeout(() => this.emitRecords(), this.delay);
    } else {
      // Use microtask to ensure async behavior
      queueMicrotask(() => this.emitRecords());
    }

    // Emit options if present
    if (Object.keys(this.options).length > 0) {
      queueMicrotask(() => this.events.onOptions?.(this.options));
    }
  }

  /**
   * Clean up mock provider
   */
  destroy(): void {
    this.ready = false;
    this.events = {};
  }

  /**
   * Get current records
   */
  getRecords(): GristRecord[] {
    return this.records;
  }

  /**
   * Mock record selection
   */
  async selectRecord(recordId: number): Promise<void> {
    this.selectedRecordId = recordId;
    const record = this.records.find((r) => r.id === recordId) || null;
    this.events.onRecord?.(record);
  }

  /**
   * Get currently selected record ID (for testing)
   */
  getSelectedRecordId(): number | null {
    return this.selectedRecordId;
  }

  /**
   * Save option
   */
  async setOption(key: string, value: unknown): Promise<void> {
    this.options[key] = value;
  }

  /**
   * Get option
   */
  async getOption<T>(key: string): Promise<T | null> {
    return (this.options[key] as T) || null;
  }

  /**
   * Check if provider is ready
   */
  isReady(): boolean {
    return this.ready;
  }

  // --- Testing helpers ---

  /**
   * Update records and emit change event
   */
  setRecords(records: GristRecord[], mappings?: ColumnMappings): void {
    this.records = records;
    if (mappings) {
      this.mappings = mappings;
    }
    this.emitRecords();
  }

  /**
   * Update mappings and emit records with new mappings
   */
  setMappings(mappings: ColumnMappings): void {
    this.mappings = mappings;
    this.emitRecords();
  }

  /**
   * Simulate selecting a record (from Grist side)
   */
  simulateRecordSelect(record: GristRecord | null): void {
    this.selectedRecordId = record?.id || null;
    this.events.onRecord?.(record);
  }

  /**
   * Get current mappings (for testing)
   */
  getMappings(): ColumnMappings {
    return this.mappings;
  }

  /**
   * Get stored options (for testing)
   */
  getOptions(): Record<string, unknown> {
    return this.options;
  }

  /**
   * Emit current records to listeners
   */
  private emitRecords(): void {
    if (this.ready) {
      this.events.onRecords?.(this.records, this.mappings);
    }
  }
}

/**
 * Create sample records for testing
 */
export function createSampleRecords(count: number = 5): GristRecord[] {
  const types = ['Restaurant', 'Hôtel', 'Musée', 'Parc', 'Monument'];
  const cities = [
    { name: 'Paris', lat: 48.8566, lng: 2.3522 },
    { name: 'Lyon', lat: 45.764, lng: 4.8357 },
    { name: 'Marseille', lat: 43.2965, lng: 5.3698 },
    { name: 'Bordeaux', lat: 44.8378, lng: -0.5792 },
    { name: 'Toulouse', lat: 43.6047, lng: 1.4442 },
    { name: 'Nantes', lat: 47.2184, lng: -1.5536 },
    { name: 'Strasbourg', lat: 48.5734, lng: 7.7521 },
    { name: 'Lille', lat: 50.6292, lng: 3.0573 },
    { name: 'Nice', lat: 43.7102, lng: 7.262 },
    { name: 'Rennes', lat: 48.1173, lng: -1.6778 },
  ];

  return Array.from({ length: count }, (_, i) => {
    const city = cities[i % cities.length];
    const type = types[i % types.length];
    return {
      id: i + 1,
      name: `${type} ${city.name}`,
      latitude: city.lat + (Math.random() - 0.5) * 0.1,
      longitude: city.lng + (Math.random() - 0.5) * 0.1,
      type: type,
      info: `Information sur ${type} à ${city.name}`,
    };
  });
}

/**
 * Default column mappings for sample records
 */
export const DEFAULT_SAMPLE_MAPPINGS: ColumnMappings = {
  Name: 'name',
  Latitude: 'latitude',
  Longitude: 'longitude',
  Type: 'type',
  Info: ['info'],
};
